import React, { useCallback } from "react";
import { makeStyles, Theme, ThemeProvider } from "@material-ui/core/styles";
import { useTenantImage } from "@ploy-ui/tenants";

import merge from "lodash/merge";
import { ImageFit } from "@ploy-ui/core";

const useBannerStyles = makeStyles<
	Theme,
	{
		bannerBackgroundImage?: string | null;
		useTransparentBackground?: boolean;
	}
>(
	{
		banner: {
			height: "335px",
			position: "absolute",
			backgroundPosition: "top",
			top: 0,
			left: 0,
			width: "100%",
			zIndex: -1
		}
	},
	{
		name: "DployFormViewsBanner"
	}
);

export function LayoutWithBanner(
	props: React.PropsWithChildren<{ disableBanner?: boolean }>
) {
	const { children, disableBanner } = props;

	let bannerBackgroundImage = useTenantImage("form_web_header");
	if (disableBanner) bannerBackgroundImage = null;
	const hasBanner = bannerBackgroundImage != null;

	const classes = useBannerStyles({
		bannerBackgroundImage
	});

	const formWebThemeOverride = useCallback(
		(theme: Theme): Theme => ({
			...theme,
			/**
			 * I use merge() here to ensure we keep preexisting overrides and _all_ of their properties (deep merge), if you want to override some components completely, use eg.
			 * @example
			 * {...,
			 * 	overrides: {
			 * 		...merge({}, theme.overrides, {...existing code}),
			 * 		...{my specific overrides}
			 * 	}
			 * }
			 */
			overrides: merge({}, theme.overrides, {
				DirWebStepper: {
					...(hasBanner
						? {
								label: {
									color: theme.palette.primary.contrastText,

									"&$activeLabel": {
										color: theme.palette.primary.contrastText
									},

									"&$completedLabel": {
										color: theme.palette.primary.contrastText
									}
								},
								activeLabel: {},
								completedLabel: {}
						  }
						: undefined)
				},
				DirWebStepperConnector: {
					...(hasBanner
						? {
								line: {
									borderColor: theme.palette.primary.contrastText
								}
						  }
						: undefined)
				},
				DirWebStepperStepIcon: {
					...(hasBanner
						? {
								root: {
									color: theme.palette.primary.contrastText,
									borderColor: theme.palette.primary.contrastText
								}
						  }
						: undefined)
				}
			})
		}),
		[hasBanner]
	);

	return (
		<ThemeProvider theme={formWebThemeOverride}>
			<ImageFit
				className={classes.banner}
				src={bannerBackgroundImage}
				alt="bannerBackgroundImage"
				fit="cover"
				//undefined to not override default position value in classes.banner. This will allow tenants to override.
				//TODO: Use this position props when developing image position support for brands
				position="undefined"
			/>
			{children}
		</ThemeProvider>
	);
}
